'use client';

import * as React from 'react';

import type { FileDropItem } from 'react-aria-components';
import { Button, DropZone, FileTrigger } from 'react-aria-components';

import { Exclamation, UploadCloud } from '@avodah-inc/ui/icons';
import { cn } from '@avodah-inc/ui/utils';

import { Typography } from '../typography';

export type FileDropzoneProps = React.HTMLAttributes<HTMLDivElement> & {
  error: string;
  onFileChosen: (dropItems: File) => void;
  dataTestId?: string;
};

export const FileDropzone = ({
  error,
  onFileChosen,
  dataTestId,
}: FileDropzoneProps) => {
  return (
    <DropZone
      className={cn(
        'hover:bg-primary-50 flex h-40 flex-col items-center justify-center rounded-md border border-dashed',
        {
          'border-controls-primary-office': !error,
          'border-status-alert': error,
        },
      )}
      onDrop={(e) => {
        (e.items[0] as FileDropItem)
          .getFile()
          .then((file) => {
            onFileChosen(file);
          })
          .catch((_) => {
            throw new Error('Invalid file');
          });
      }}
    >
      <FileTrigger
        onSelect={(e) => {
          const file = e?.item(0);
          if (file !== null && file !== undefined) {
            onFileChosen(file);
          }
        }}
      >
        <div>
          <Button
            className="focus:outline-none active:outline-none"
            data-testid={dataTestId}
          >
            <section className="flex w-full flex-col items-center justify-center">
              <UploadCloud
                width={64}
                height={64}
                className="fill-icon-office"
              />
              <Typography className="text-onLight-medium" variant="h4">
                Drag and drop the file here or click
              </Typography>
            </section>
          </Button>
          {error ? (
            <section className="mt-3 flex w-full items-center justify-center">
              <Exclamation width={20} height={20} className="mr-2" />
              <Typography className="text-status-alert" variant="h5">
                {error}
              </Typography>
            </section>
          ) : (
            <div></div>
          )}
        </div>
      </FileTrigger>
    </DropZone>
  );
};

FileDropzone.displayName = 'FileDropzone';
